import { useEffect } from 'react';

/**
 * Wire global Enter-key submission to a primary action.
 *
 * The JoyForm-style UX shows "press Enter ↵" next to every primary CTA.
 * To make the hint functional (not decorative), each step that displays
 * the hint calls useEnterKey(handler, { enabled }).
 *
 * Behaviour:
 *   - Listens on document keydown for "Enter".
 *   - Ignores if the active element is a textarea (Enter = newline there).
 *   - Ignores if the active element is a button — let native click
 *     activation win.
 *   - Ignores if any modifier (Shift / Ctrl / Alt / Meta) is held.
 *   - Calls handler() if enabled is true (default).
 *
 * Pass enabled=false when the form is invalid or already submitting.
 */
export default function useEnterKey(handler, { enabled = true, requireMeta = false } = {}) {
  useEffect(() => {
    if (!enabled || typeof handler !== 'function') return undefined;

    const onKeyDown = (e) => {
      if (e.key !== 'Enter') return;
      if (requireMeta) {
        // Spec: ⌘+Enter (macOS) / Ctrl+Enter (Windows). Either modifier
        // is acceptable; bare Enter is ignored. Used on Selfie Upload
        // because plain Enter inside the file picker context is too
        // easy to fire by accident.
        if (!e.metaKey && !e.ctrlKey) return;
        if (e.shiftKey || e.altKey) return;
      } else {
        if (e.shiftKey || e.ctrlKey || e.altKey || e.metaKey) return;
      }
      const active = document.activeElement;
      if (active && active.tagName === 'TEXTAREA') return;
      if (active && active.tagName === 'BUTTON') return;
      e.preventDefault();
      handler();
    };

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [handler, enabled, requireMeta]);
}
